import React, { useEffect } from 'react';
import moment from 'moment';
import { useStore } from '../../../../../stores/root';

// assets

// services
import GeneralService from '../../../../../services/general';
import PermissionsService from '../../../../../services/permissions';

// props
import { IOrderPriceUpdateResourceShortProps } from '../../../../../props/orders/priceUpdates';

// components
import { DefaultButton, ICommandBarItemProps, MessageBarType, Panel, PanelType, PrimaryButton, Spinner, SpinnerSize, Stack, TextField } from '@fluentui/react';
import Label from '../../../../typography/label';
import Text from '../../../../typography/text';
import Details from '../../../../typography/details';
import OrderPriceUpdateService from '../../../../../services/orders/priceUpdates';
import SimpleMessageBarComponent from '../../../../feedbacks/simpleMessageBar';
import ValidationService from '../../../../../services/validation';
import { faCheck } from '@fortawesome/pro-light-svg-icons';

type ApproveOrderPriceUpdateFormProps = {
    requestId: string;
    onDismissed(refresh?: boolean): void;
}

const ApproveOrderPriceUpdateForm: React.FC<ApproveOrderPriceUpdateFormProps> = (props: ApproveOrderPriceUpdateFormProps) => {
    const { banner, user } = useStore();

    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [request, setRequest] = React.useState<IOrderPriceUpdateResourceShortProps | undefined>();
    const [justification, setJustification] = React.useState<string>("");

    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | undefined>();

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        const _request = await OrderPriceUpdateService.get(props.requestId);

        setRequest(_request);
        setLoaded(true);
    }

    const _onSubmit = async () => {
        try {
            setSubmitting(true);

            const fd = new FormData();
            fd.append("justification", justification);

            await OrderPriceUpdateService.approve(props.requestId, fd);

            banner.add({
                key: 'approve_order_price_update_success',
                variant: 'success',
                icon: faCheck,
                text: `Request to update price has been approved`
            });
            props.onDismissed(true);
        } catch (e) {
            setSubmitting(false);
        }
    }

    return <Panel headerText={'Approve Price Update Request'}
        isOpen={true}
        type={PanelType.medium}
        onDismiss={() => props.onDismissed(false)}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                {
                    !submitting ? (
                        <>
                            <PrimaryButton text={"Submit"} disabled={error !== undefined || justification === ""} onClick={_onSubmit} />
                            <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed(false) }} />
                        </>
                    ) : null
                }
                {submitting ? <Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Submitting request ..."} /> : null}
            </Stack>;
        }}>
        <Stack tokens={{ childrenGap: 20 }}>
            {!loaded ? <Stack horizontalAlign={"baseline"}><Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Retrieving details ..."} /></Stack> : null}
            {
                loaded && request ? <>
                    {request.container ? <>
                        <Label>Container Details</Label>
                        <Stack horizontal tokens={{ childrenGap: 20 }}>
                            <Details title='Container number' text={request.container.containerNumber || "-"} style={{ width: '50%' }} />
                            <Details title='Size' text={request.container.size || "-"} style={{ width: '50%' }} />
                        </Stack>
                        <Stack horizontal tokens={{ childrenGap: 20 }}>
                            <Details title='Seal number' text={request.container.sealNumber || "-"} style={{ width: '50%' }} />
                            <Details title='RO number' text={request.container.roNumber || "-"} style={{ width: '50%' }} />
                        </Stack>
                    </> : null}
                    {request.truck ? <>
                        <Label>Truck Details</Label>
                        <Details title='Registration number' text={request.truck.vehicleRegistrationNumber || "-"} />
                        <Stack horizontal tokens={{ childrenGap: 20 }}>
                            <Details title='Pick-up address' text={request.truck.originAddress || "-"} style={{ width: '50%' }} />
                            <Details title='Drop-off address' text={request.truck.destinationAddress || "-"} style={{ width: '50%' }} />
                        </Stack>
                    </> : null}
                    {request.goods ? <>
                        <Label>Truck Details</Label>
                        <Stack horizontal tokens={{ childrenGap: 20 }}>
                            <Details title='Name' text={request.goods.goods.name || "-"} style={{ width: '50%' }} />
                            <Details title='Quantity' text={request.goods.quantityAmount + " " + (request.goods.goods.quantityUnit || "")} style={{ width: '50%' }} />
                        </Stack>
                        <Details title='Notes' text={request.goods.notes || "-"} />
                    </> : null}
                    <Stack className={'divider'}></Stack>
                    <Label size={'small'}>Pricing Details</Label>
                    <Details title='Pricing method' text={request.pricingMethod} />
                    <Stack>
                        <Details title={'Price'} text={`Rp. ${GeneralService.getNumberWithSeparator(request.price || "0")}`} />
                        <Stack horizontal tokens={{ childrenGap: 5 }}>
                            {request.includePPn ? <Text size={'small'} className={'color-green'}>(include PPn)</Text> : null}
                            {request.includePPh ? <Text size={'small'} className={'color-green'}>(include PPh)</Text> : null}
                            {!request.includePPn ? <Text size={'small'} className={'color-red'}>(exclude PPn)</Text> : null}
                            {!request.includePPh ? <Text size={'small'} className={'color-red'}>(exclude PPh)</Text> : null}
                        </Stack>
                    </Stack>
                    <Stack horizontal tokens={{ childrenGap: 20 }}>
                        <Details title={`PPn (${request.ppnPercentage}%)`} text={`Rp. ${GeneralService.getNumberWithSeparator(Number(request.ppn))}`} style={{ width: '50%' }} />
                        <Details title={`PPh (${request.pphPercentage}%)`} text={`Rp. ${GeneralService.getNumberWithSeparator(Number(request.pph))}`} style={{ width: '50%' }} />
                    </Stack>
                    <Stack horizontal tokens={{ childrenGap: 20 }}>
                        <Details title={`Price (inc. PPn & PPh)`} text={`Rp. ${GeneralService.getNumberWithSeparator(request.totalPrice)}`} style={{ width: '50%' }} />
                        <Details title={`Invoice price (total price - PPh)`} text={`Rp. ${GeneralService.getNumberWithSeparator(request.invoicePrice)}`} style={{ width: '50%' }} />
                    </Stack>
                    <Stack className={'divider'}></Stack>
                    <Stack.Item>
                        <TextField required={true}
                            label={"Justification"}
                            value={justification}
                            multiline
                            resizable={false}
                            autoAdjustHeight
                            rows={5}
                            onChange={(evt, value) => {
                                const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 2000 });

                                setJustification(value || "");
                                setError(validation.message);
                            }}
                            errorMessage={error}
                            disabled={submitting} />
                    </Stack.Item>
                </> : null
            }
        </Stack>
    </Panel >
};

export default ApproveOrderPriceUpdateForm;
